import { Box, Typography, Toolbar, Button, Tooltip } from "@mui/material";
import { useMemo } from "react";
import Graph from "../../../components/Graphs/Graph";
import { CSVLink } from "react-csv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { CountType } from "../../../types/Count";
import { SummaryTable } from "./Table/SummaryTable";
import { formatCSVData } from "./tsutils";
import { useUserContext } from "../../../providers/useUserProvider";
import { Direction } from "../../../types/Direction";
import { getSummaryCount } from "./utils";
import { getTimezoneAbbr, toTemporalZonedDateTime, formatDate } from "../../../shared/lib/temporal";

const BOUNDS_SUFFIX = " Bounds";

const SummaryReport = ({ data, reportedCountType, detectionParams }) => {
    const enableCI = reportedCountType === CountType.Adjusted;
    const { timezoneDetails, selectedSite } = useUserContext();
    const timezone = timezoneDetails?.timezone || "America/Halifax";
    // Derive abbreviation (AST/ADT) based on the detection window date
    const timezoneAbbreviation = useMemo(() => {
        const basis = detectionParams?.endTimestamp ? new Date(detectionParams.endTimestamp) : new Date();
        return getTimezoneAbbr(toTemporalZonedDateTime(basis, timezone));
    }, [timezone, detectionParams?.endTimestamp]);

    const endTimestamp = detectionParams?.endTimestamp ? new Date(detectionParams.endTimestamp).getTime() : null;

    const { enableExport, hourlySeries, cumulativeNetSeries, cumulativeConfidenceIntervalSeries } = useMemo(
        () => initialize(data),
        [data, enableCI],
    );

    // Merged cumulative series: per-device CI bands are pushed only for
    // devices that meet the manual-count threshold (see initialize).
    const cumulativeSeries = useMemo(
        () => [...cumulativeNetSeries, ...(enableCI ? cumulativeConfidenceIntervalSeries : [])],
        [cumulativeNetSeries, cumulativeConfidenceIntervalSeries, enableCI],
    );

    // Derive stroke/fill arrays from the actual series order so per-device
    // CI gating renders correctly (line series get a visible stroke + opaque
    // fill; rangeArea bands are zero-stroke + translucent).
    const cumulativeStrokeWidth = useMemo(
        () => cumulativeSeries.map((s) => (s.type === "rangeArea" ? 0 : 2)),
        [cumulativeSeries],
    );
    const cumulativeFillOpacity = useMemo(
        () => cumulativeSeries.map((s) => (s.type === "rangeArea" ? 0.24 : 1)),
        [cumulativeSeries],
    );

    const baseChartOptions = {
        animations: {
            enabled: false,
        },
        toolbar: {
            tools: {
                reset: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
            },
        },
        zoom: {
            enabled: true,
            type: "x",
            autoScaleYaxis: true,
            allowMouseWheelZoom: false,
            zoomedArea: {
                fill: {
                    color: "#90CAF9",
                    opacity: 0.4,
                },
                stroke: {
                    color: "#0D47A1",
                    opacity: 0.4,
                    width: 1,
                },
            },
        },
    };

    const baseOptions = {
        legend: {
            showForZeroSeries: true,
            showForNullSeries: true,
            showForSingleSeries: true,
        },
    };

    const cumulativeChartOptions = {
        ...baseOptions,
        chart: {
            id: "cumulativeChart",
            type: enableCI ? "rangeArea" : "line",
            ...baseChartOptions,
        },
        colors: ["#48A3A1", "#8BD4DF", "#48A3A1", "#8BD4DF"],
        xaxis: {
            type: "datetime",
            title: {
                text: `Timestamp (${timezoneAbbreviation})`,
                style: {
                    color: "#000000",
                    fontSize: "15px",
                },
            },
            labels: {
                datetimeFormatter: {
                    year: "yyyy",
                    month: "MMM 'yy",
                    day: "dd MMM",
                    hour: "HH:mm",
                },
                datetimeUTC: false,
            },
            padding: {
                top: 0,
                right: 50,
                bottom: 0,
                left: 50,
            },
            max: endTimestamp,
        },
        dataLabels: {
            enabled: false,
        },
        yaxis: {
            labels: {
                style: {
                    colour: "#000000",
                    fontSize: "15px",
                },
                formatter: function (value) {
                    return value?.toLocaleString();
                },
            },
            title: {
                text: `${reportedCountType} Cumulative Counts`,
                style: {
                    color: "#000000",
                    fontSize: "15px",
                },
            },
        },
        stroke: {
            curve: "straight",
            width: cumulativeStrokeWidth,
        },
        fill: {
            opacity: cumulativeFillOpacity,
        },
        tooltip: {
            x: {
                formatter: function (val) {
                    const zdt = toTemporalZonedDateTime(new Date(val), timezone);
                    return formatDate(zdt, "DD/MM/YYYY HH:mm:ss");
                },
            },
            followCursor: true,
            custom: function ({ dataPointIndex, w }) {
                const tooltipData = {};

                w.config.series.forEach((currentSeries, index) => {
                    if (w.globals.collapsedSeries.includes(index)) {
                        return;
                    }

                    const seriesName = currentSeries.name.replace(BOUNDS_SUFFIX, "");

                    const currentDataPoint = currentSeries.data?.[dataPointIndex] || { y: null };

                    if (!tooltipData[seriesName]) {
                        tooltipData[seriesName] = {};
                    }

                    if (enableCI) {
                        if (currentSeries.type === "rangeArea") {
                            const lower = currentDataPoint.y?.[0];
                            const upper = currentDataPoint.y?.[1];

                            if (lower != null) tooltipData[seriesName].lower = lower;
                            if (upper != null) tooltipData[seriesName].upper = upper;
                        } else {
                            // Only set isUnidirectional when the source point
                            // actually has it; otherwise we'd write `undefined`
                            // into tooltipData and the Object.entries iteration
                            // below would throw on `data.value`. Hits when
                            // dataPointIndex is out of range and we fall through
                            // to the `{ y: null }` placeholder.
                            if (currentDataPoint.isUnidirectional !== undefined) {
                                tooltipData["isUnidirectional"] = currentDataPoint.isUnidirectional;
                            }
                            const adjusted = currentDataPoint.y;
                            if (adjusted != null) tooltipData[seriesName].value = adjusted;
                        }
                    } else {
                        const total = currentDataPoint.y;
                        const upstream = currentDataPoint.upstream;
                        const downstream = currentDataPoint.downstream;
                        const counts = currentDataPoint.counts;

                        if (total != null) tooltipData[seriesName].value = total;
                        if (upstream != null) tooltipData[seriesName].upstream = upstream;
                        if (downstream != null) tooltipData[seriesName].downstream = downstream;
                        if (counts != null) tooltipData[seriesName].counts = counts;
                    }
                });

                const formattedTooltip = Object.entries(tooltipData)
                    .map(([name, data]) => {
                        if (!data.value) {
                            return null;
                        }

                        if (enableCI) {
                            if (data.lower != null && data.upper != null) {
                                if (tooltipData.isUnidirectional) {
                                    return `<div><b>${name}</b>: Lower Bound ${data.lower}, Adjusted Counts ${data.value}, Upper Bound ${data.upper}</div>`;
                                } else {
                                    return `<div><b>${name}</b>: Lower Bound ${data.lower}, Net Adjusted ${data.value}, Upper Bound ${data.upper}</div>`;
                                }
                            } else {
                                return `<div><b>${name}</b>: Adjusted ${data.value}</div>`;
                            }
                        } else {
                            if (data.counts) {
                                return `<div>
                                        <b>${name}</b>:
                                        Counts: ${data.counts}
                                        </div>`;
                            }

                            return `<div>
                                        <b>${name}</b>:
                                        ${data.upstream ? ` Upstream: ${data.upstream}` : ""}
                                        ${data.downstream ? ` Downstream: ${data.downstream}` : ""}
                                        Net: ${data.value}
                                    </div>`;
                        }
                    })
                    .join("");

                return `<div class="custom-tooltip">
                    ${formattedTooltip}
                </div>`;
            },
        },
    };

    const lineChartOptions = {
        chart: {
            type: "line",
            ...baseChartOptions,
        },
        colors: ["#48A3A1", "#8BD4DF"],
        stroke: {
            width: 2,
        },
        tooltip: {
            x: {
                formatter: function (val) {
                    const zdt = toTemporalZonedDateTime(new Date(val), timezone);
                    return formatDate(zdt, "DD/MM/YYYY HH:mm:ss");
                },
            },
            y: {
                formatter: function (value, { seriesIndex, dataPointIndex, w }) {
                    const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];

                    if (value == null) {
                        return null;
                    }

                    if (!dataPoint) {
                        return `Net: ${value}`;
                    }

                    const { upstream, downstream, counts } = dataPoint;
                    if (counts !== undefined) {
                        return `Counts: ${counts}`;
                    }

                    let parts = [];
                    if (upstream !== undefined && downstream !== undefined) {
                        parts.push(`Upstream: ${upstream}`);
                        parts.push(`Downstream: ${downstream}`);
                        parts.push(`Net: ${value}`);
                    } else if (upstream !== undefined) {
                        parts.push(`Upstream: ${upstream}`);
                    } else if (downstream !== undefined) {
                        parts.push(`Downstream: ${downstream}`);
                    }
                    return parts.join(" ");
                },
            },
        },

        xaxis: {
            type: "datetime",
            title: {
                text: `Timestamp (${timezoneAbbreviation})`,
                style: {
                    color: "#000000",
                    fontSize: "15px",
                },
            },
            max: endTimestamp,
            labels: {
                datetimeFormatter: {
                    year: "yyyy",
                    month: "MMM 'yy",
                    day: "dd MMM",
                    hour: "HH:mm",
                },
                datetimeUTC: false,
            },
            padding: {
                top: 0,
                right: 50,
                bottom: 0,
                left: 50,
            },
        },
    };

    const hourlyGraphOptions = {
        ...lineChartOptions,
        yaxis: {
            labels: {
                style: {
                    colour: "#000000",
                    fontSize: "15px",
                },
                formatter: function (value) {
                    return value?.toLocaleString();
                },
            },
            title: {
                text: `${reportedCountType} Hourly Counts`,
                style: {
                    color: "#000000",
                    fontSize: "15px",
                },
            },
        },
    };

    function initialize(data) {
        const enableExport = Object.keys(data).every((key) => data[key].counts.length !== 0);

        const series = {
            cumulativeConfidenceIntervalSeries: [],
            cumulativeNetSeries: [],
            hourlySeries: [],
        };

        Object.values(data)
            .filter((deviceCounts) => deviceCounts.device.configs?.[0].mode === "COUNTS_ENABLED")
            .forEach((deviceCounts) => {
                const isUnidirectional = deviceCounts.device.configs?.[0]?.directionality === "UNIDIRECTIONAL";
                const direction =
                    deviceCounts.device.configs?.[0]?.directions?.dir1 === "UPSTREAM"
                        ? Direction.Upstream
                        : Direction.Downstream;
                // Per-device CI gating: even when the global toggle is on,
                // a device must have ≥30 manual counts (computed upstream
                // in useProcessedCounts) to render its bias-corrected
                // Adjusted line and CI band. Devices below the threshold
                // fall back to Predicted to avoid implying precision we
                // don't have.
                const useCIForDevice = enableCI && deviceCounts.ciEligible;
                const deviceCountType = useCIForDevice ? CountType.Adjusted : CountType.Predicted;

                let cumulativeNetPoint = {
                    name: deviceCounts.device.displayName,
                    type: "line",
                    zIndex: 1,
                    data: deviceCounts.counts.map((item) => {
                        const baseData = {
                            x: item.timestamp,
                            y: item.imputed
                                ? null
                                : Math.floor(
                                      isUnidirectional
                                          ? (item.cumulative?.[direction]?.[deviceCountType] ?? 0)
                                          : (item.cumulative?.Net?.[deviceCountType] ?? 0),
                                  ),
                            isUnidirectional: isUnidirectional,
                        };
                        if (isUnidirectional) {
                            return {
                                ...baseData,
                                counts: item.imputed
                                    ? null
                                    : Math.floor(item.cumulative?.[direction]?.[deviceCountType] ?? 0),
                            };
                        }
                        return {
                            ...baseData,
                            upstream: item.imputed
                                ? null
                                : Math.floor(item.cumulative?.Upstream?.[deviceCountType] ?? 0),
                            downstream: item.imputed
                                ? null
                                : Math.floor(item.cumulative?.Downstream?.[deviceCountType] ?? 0),
                        };
                    }),
                };

                if (useCIForDevice) {
                    series.cumulativeConfidenceIntervalSeries.push({
                        name: `${deviceCounts.device.displayName}${BOUNDS_SUFFIX}`,
                        type: "rangeArea",
                        zIndex: 0,
                        data: deviceCounts.counts.map((item) => ({
                            x: item.timestamp,
                            y: item.imputed
                                ? [null, null]
                                : isUnidirectional
                                  ? [
                                        Math.floor(item.cumulative?.[direction]?.LowerBound ?? 0),
                                        Math.floor(item.cumulative?.[direction]?.UpperBound ?? 0),
                                    ]
                                  : [
                                        Math.floor(item.cumulative?.Net?.LowerBound ?? 0),
                                        Math.floor(item.cumulative?.Net?.UpperBound ?? 0),
                                    ],
                            adjusted: isUnidirectional
                                ? Math.floor(item.cumulative?.[direction]?.Adjusted ?? 0)
                                : Math.floor(item.cumulative?.Net?.Adjusted ?? 0),
                        })),
                    });
                }

                series.cumulativeNetSeries.push(cumulativeNetPoint);

                series.hourlySeries.push({
                    name: deviceCounts.device.displayName,
                    type: "line",
                    zIndex: 1,
                    data: deviceCounts.counts.map((item) => {
                        const baseData = {
                            x: item.timestamp,
                            y: item.imputed
                                ? null
                                : Math.floor(
                                      isUnidirectional
                                          ? (item.instant?.[direction]?.[deviceCountType] ?? 0)
                                          : (item.instant?.Net?.[deviceCountType] ?? 0),
                                  ),
                        };
                        if (isUnidirectional) {
                            return {
                                ...baseData,
                                counts: item.imputed
                                    ? null
                                    : Math.floor(item.instant?.[direction]?.[deviceCountType] ?? 0),
                            };
                        } else {
                            return {
                                ...baseData,
                                upstream: item.imputed
                                    ? "n/a"
                                    : Math.floor(item.instant?.Upstream?.[deviceCountType] ?? 0),
                                downstream: item.imputed
                                    ? "n/a"
                                    : Math.floor(item.instant?.Downstream?.[deviceCountType] ?? 0),
                            };
                        }
                    }),
                });
            });

        return {
            enableExport,
            ...series,
        };
    }

    const summaryCounts = Object.values(data).map((deviceCounts) => {
        return getSummaryCount(deviceCounts);
    });

    return (
        <>
            <Box>
                <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                    <Typography
                        sx={{ flex: "1 1 100%", color: "#043c4a" }}
                        variant="h4"
                        id="tableTitle"
                        component="div"
                    >
                        Season Summary
                    </Typography>
                    <CSVLink
                        data={formatCSVData(data, reportedCountType, timezone, "date")}
                        filename={`${selectedSite.name}_${String(reportedCountType).toLowerCase()}_season_report.csv`}
                        target="_blank"
                        enabled={`${enableExport}`}
                    >
                        <Tooltip title="Export Season Summary (CSV)">
                            <span>
                                <Button
                                    sx={{
                                        background: "#4eafb2",
                                        "&:hover": {
                                            backgroundColor: "#043c4a",
                                            boxShadow: "none",
                                        },
                                    }}
                                    startIcon={<FileDownloadIcon />}
                                    disabled={!enableExport}
                                    variant="contained"
                                >
                                    Export
                                </Button>
                            </span>
                        </Tooltip>
                    </CSVLink>
                </Toolbar>
                {summaryCounts.every((deviceCount) => deviceCount.timestamp !== undefined) ? (
                    <SummaryTable data={summaryCounts} showCI={enableCI} label="season-summary-table" />
                ) : (
                    <Typography>Nothing to display</Typography>
                )}
            </Box>

            <br />
            <Box className="FishcountsGraph">
                <Typography variant="h4" sx={{ color: "#043C4A" }} component="div">
                    Cumulative Counts
                </Typography>

                <Graph options={cumulativeChartOptions} series={cumulativeSeries}></Graph>
            </Box>
            <br />
            <Box className="FishcountsGraph">
                <Typography variant="h4" sx={{ color: "#043C4A" }} component="div">
                    Hourly Counts
                </Typography>
                <Graph options={hourlyGraphOptions} series={hourlySeries}></Graph>
            </Box>
        </>
    );
};

export default SummaryReport;
